import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supermarketService, CreateSupermarketData } from '../services/supermarketService';
import { getAllCategories, getCategoryIcon } from '../utils/categories';
import { getCategoryEmoji } from '../utils/categoryIcons';
import { extractAndSaveCustomCategories } from '../utils/supermarketCategories';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';
import Toast from '../components/Toast';
import { ArrowLeft, Store, MapPin, Layers, Tags, Plus, X, Check } from 'lucide-react';

interface Zone {
  name: string;
  icon: string;
  categories: string[];
}

const SUPERMARKET_CHAINS = [
  'REWE',
  'EDEKA',
  'ALDI',
  'LIDL',
  'PENNY',
  'Netto',
  'Kaufland',
  'real',
  'Globus',
  'Sonstiges'
];

// Zone icons are now auto-generated from zone names using getCategoryEmoji()
// const ZONE_ICONS = ['🥬', '🥖', '🏪', '❄️', '🍬', '🥛', '🍺', '🧴', '🧹', '📦', '🎯', '🏁'];

export default function SupermarketCreate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id: supermarketId } = useParams<{ id: string }>();
  const isEditMode = !!supermarketId;
  const [step, setStep] = useState(1);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);

  // Step 1: Basic Info
  const [chain, setChain] = useState('');
  const [name, setName] = useState('');
  const [street, setStreet] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [city, setCity] = useState('');
  const [similarFound, setSimilarFound] = useState<any[]>([]);

  // Step 2: Zones
  const [zones, setZones] = useState<Zone[]>([
    { name: 'Obst & Gemüse', icon: '🥬', categories: [] },
    { name: 'Backwaren', icon: '🥖', categories: [] },
    { name: 'Kühlregal', icon: '❄️', categories: [] }
  ]);

  // Step 3: Categories
  const [currentZoneIndex, setCurrentZoneIndex] = useState(0);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showAddCategory, setShowAddCategory] = useState(false);
  
  // Drag & Drop
  const [draggedZoneIndex, setDraggedZoneIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);

  useEffect(() => {
    loadCategories();
    if (isEditMode && supermarketId) {
      loadSupermarket();
    }
  }, []);

  const loadSupermarket = async () => {
    try {
      setInitialLoading(true);
      const data = await supermarketService.getById(supermarketId!);
      
      // Check if user is trying to edit a locked template they don't own
      if (data.userLocked && data.firstCreatedById !== user?.id) {
        setToast({
          message: 'Diese Vorlage ist gesperrt und kann nicht bearbeitet werden',
          type: 'error'
        });
        setTimeout(() => navigate('/supermarkets'), 2000);
        return;
      }
      
      setChain(data.chain);
      setName(data.name || '');
      setStreet(data.street);
      setZipCode(data.zipCode);
      setCity(data.city);
      
      // Convert zones from API format
      const loadedZones = data.zones.map(z => ({
        name: z.name,
        icon: z.icon || getCategoryEmoji(z.name),
        categories: z.categoryMappings.map(cm => cm.categoryName)
      }));
      setZones(loadedZones);
      
      // Extract all used categories and add to available categories
      const usedCategories = loadedZones.flatMap(z => z.categories);
      const uniqueUsedCategories = [...new Set(usedCategories)];
      
      setAvailableCategories(prevCats => {
        const allCategories = [...prevCats];
        uniqueUsedCategories.forEach(cat => {
          if (!allCategories.includes(cat)) {
            allCategories.push(cat);
          }
        });
        return allCategories;
      });

      // Save custom categories to localStorage for persistence
      extractAndSaveCustomCategories(data);
      
      // Reload categories to include newly saved custom categories
      loadCategories();
    } catch (error: any) {
      console.error('Error loading supermarket:', error);
      setToast({
        message: error.response?.data?.error || 'Fehler beim Laden',
        type: 'error'
      });
      navigate('/supermarkets');
    } finally {
      setInitialLoading(false);
    }
  };

  const loadCategories = async () => {
    // Standard categories
    const cats = getAllCategories();
    const standardCats = cats.map(([name]) => name);
    
    // Load custom categories from localStorage
    const savedCategories = localStorage.getItem('customCategories');
    const customCats = savedCategories ? JSON.parse(savedCategories) : [];
    
    // Merge and deduplicate
    const allCats = [...new Set([...standardCats, ...customCats])];
    
    setAvailableCategories(allCats);
  };

  const addCustomCategory = () => {
    if (!newCategoryName.trim()) return;
    
    if (availableCategories.includes(newCategoryName.trim())) {
      setToast({ message: 'Kategorie existiert bereits', type: 'warning' });
      return;
    }
    
    setAvailableCategories([...availableCategories, newCategoryName.trim()]);
    setNewCategoryName('');
    setShowAddCategory(false);
    setToast({ message: 'Kategorie hinzugefügt', type: 'success' });
  };

  const checkDuplicates = async () => {
    if (!chain || !street || !zipCode) return;

    try {
      const result = await supermarketService.search({ 
        chain, 
        street, 
        zipCode, 
        city,
        excludeId: isEditMode ? supermarketId : undefined 
      });
      
      if (result.exact) {
        setSimilarFound([result.exact]);
        setToast({
          message: 'Ein identischer Supermarkt existiert bereits!',
          type: 'warning'
        });
      } else if (result.similar.length > 0) {
        setSimilarFound(result.similar);
        setToast({
          message: `${result.similar.length} ähnliche(r) Supermarkt gefunden`,
          type: 'info'
        });
      } else {
        setSimilarFound([]);
      }
    } catch (error) {
      console.error('Error checking duplicates:', error);
    }
  };

  const handleStep1Next = () => {
    if (!chain || !street || !zipCode || !city) {
      setToast({ message: 'Bitte fülle alle Pflichtfelder aus', type: 'error' });
      return;
    }
    checkDuplicates();
    setStep(2);
  };

  const handleStep2Next = () => {
    if (zones.length === 0) {
      setToast({ message: 'Bitte lege mindestens eine Zone an', type: 'error' });
      return;
    }
    setCurrentZoneIndex(0);
    setStep(3);
  };

  const handleCategoryAssignment = () => {
    if (currentZoneIndex < zones.length - 1) {
      setCurrentZoneIndex(currentZoneIndex + 1);
    } else {
      handleSubmit();
    }
  };

  const handleCategoryBack = () => {
    if (currentZoneIndex > 0) {
      setCurrentZoneIndex(currentZoneIndex - 1);
    } else {
      setStep(2);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const data: CreateSupermarketData = {
        chain,
        name: name || undefined,
        street,
        zipCode,
        city,
        zones
      };

      if (isEditMode && supermarketId) {
        await supermarketService.update(supermarketId, data);
        setToast({ message: 'Supermarkt erfolgreich aktualisiert!', type: 'success' });
      } else {
        await supermarketService.create(data);
        setToast({ message: 'Supermarkt erfolgreich erstellt!', type: 'success' });
      }
      
      setTimeout(() => {
        navigate('/supermarkets');
      }, 1500);
    } catch (error: any) {
      console.error('Error creating supermarket:', error);
      setToast({
        message: error.response?.data?.error || 'Fehler beim Erstellen',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const addZone = () => {
    setZones([...zones, { name: '', icon: '�️', categories: [] }]);
  };

  const removeZone = (index: number) => {
    setZones(zones.filter((_, i) => i !== index));
  };

  const updateZone = (index: number, field: keyof Zone, value: any) => {
    const updated = [...zones];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-generate icon from zone name
    if (field === 'name' && typeof value === 'string') {
      updated[index].icon = getCategoryEmoji(value);
    }
    
    setZones(updated);
  };

  const moveZone = (fromIndex: number, toIndex: number) => {
    const updated = [...zones];
    const [movedZone] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, movedZone);
    setZones(updated);
  };

  const handleDragStart = (index: number) => {
    setDraggedZoneIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedZoneIndex !== null && draggedZoneIndex !== index) {
      setDropTargetIndex(index);
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedZoneIndex !== null && draggedZoneIndex !== dropIndex) {
      moveZone(draggedZoneIndex, dropIndex);
    }
    setDraggedZoneIndex(null);
    setDropTargetIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedZoneIndex(null);
    setDropTargetIndex(null);
  };

  const toggleCategory = (zoneIndex: number, category: string) => {
    const updated = [...zones];
    const categories = updated[zoneIndex].categories;
    
    if (categories.includes(category)) {
      updated[zoneIndex].categories = categories.filter(c => c !== category);
    } else {
      updated[zoneIndex].categories = [...categories, category];
    }
    
    setZones(updated);
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-secondary)' }}>
      {/* Header */}
      <header style={{ 
        backgroundColor: 'var(--bg-primary)',
        padding: '1rem 1.5rem',
        borderBottom: '1px solid var(--border-color)'
      }}>
        <div className="container">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
            <button
              onClick={() => step > 1 ? setStep(step - 1) : navigate('/supermarkets')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                padding: '0.5rem',
                display: 'flex'
              }}
            >
              <ArrowLeft size={24} />
            </button>
            <Logo variant="dark" />
          </div>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span>🏪</span>
            {isEditMode ? 'Supermarkt bearbeiten' : 'Neuen Supermarkt anlegen'}
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '0.25rem' }}>
            Schritt {step} von 3
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="container" style={{ paddingTop: '1rem' }}>
        {/* Progress */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          {[1, 2, 3].map(i => (
            <div
              key={i}
              style={{
                flex: 1,
                height: '4px',
                backgroundColor: i <= step ? 'var(--primary)' : 'var(--border-color)',
                borderRadius: '2px',
                transition: 'background-color 0.3s'
              }}
            />
          ))}
        </div>

        {/* Form Content */}
        <div style={{
          maxWidth: '800px',
          margin: '0 auto'
        }}>
        {initialLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="spinner" style={{ margin: '0 auto 1rem' }}></div>
            <p style={{ color: 'var(--text-secondary)' }}>Lade Supermarkt-Daten...</p>
          </div>
        ) : (
          <>
        {step === 1 && (
          <Step1BasicInfo
            chain={chain}
            setChain={setChain}
            name={name}
            setName={setName}
            street={street}
            setStreet={setStreet}
            zipCode={zipCode}
            setZipCode={setZipCode}
            city={city}
            setCity={setCity}
            similarFound={similarFound}
            onNext={handleStep1Next}
            onUseSimilar={(similar) => {
              navigate(`/supermarkets/${similar.id}/edit`);
            }}
          />
        )}

        {step === 2 && (
          <Step2Zones
            zones={zones}
            onAddZone={addZone}
            onRemoveZone={removeZone}
            onUpdateZone={updateZone}
            onNext={handleStep2Next}
            onBack={() => setStep(1)}
            draggedZoneIndex={draggedZoneIndex}
            dropTargetIndex={dropTargetIndex}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
          />
        )}

        {step === 3 && (
          <Step3Categories
            zones={zones}
            currentZoneIndex={currentZoneIndex}
            availableCategories={availableCategories}
            onToggleCategory={toggleCategory}
            onNext={handleCategoryAssignment}
            onBack={handleCategoryBack}
            isLastZone={currentZoneIndex === zones.length - 1}
            loading={loading}
            showAddCategory={showAddCategory}
            setShowAddCategory={setShowAddCategory}
            newCategoryName={newCategoryName}
            setNewCategoryName={setNewCategoryName}
            onAddCustomCategory={addCustomCategory}
          />
        )}
        </>
        )}
        </div>
      </main>

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

interface Step1BasicInfoProps {
  chain: string;
  setChain: (value: string) => void;
  name: string;
  setName: (value: string) => void;
  street: string;
  setStreet: (value: string) => void;
  zipCode: string;
  setZipCode: (value: string) => void;
  city: string;
  setCity: (value: string) => void;
  similarFound: any[];
  onNext: () => void;
  onUseSimilar: (similar: any) => void;
}

// Step 1: Basic Info Component
function Step1BasicInfo({ chain, setChain, name, setName, street, setStreet, zipCode, setZipCode, city, setCity, similarFound, onNext, onUseSimilar }: Step1BasicInfoProps) {
  return (
    <div style={{
      backgroundColor: 'var(--bg-primary)',
      borderRadius: '12px',
      padding: '2rem',
      boxShadow: 'var(--shadow-md)'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1.5rem'
      }}>
        <Store size={24} style={{ color: 'var(--primary)' }} />
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>
          Basis-Informationen
        </h2>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <div>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: '500',
            color: 'var(--text-primary)'
          }}>
            Supermarkt-Kette *
          </label>
          <select
            value={chain}
            onChange={(e) => setChain(e.target.value)}
            className="input"
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: '8px',
              fontSize: '1rem'
            }}
          >
            <option value="">Bitte wählen...</option>
            {SUPERMARKET_CHAINS.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: '500',
            color: 'var(--text-primary)'
          }}>
            Filialname (optional)
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z.B. REWE Center, EDEKA Neukauf"
            className="input"
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: '8px',
              fontSize: '1rem'
            }}
          />
        </div>

        <div>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: '500',
            color: 'var(--text-primary)'
          }}>
            Straße & Hausnummer *
          </label>
          <input
            type="text"
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            placeholder="z.B. Hauptstraße 45"
            className="input"
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: '8px',
              fontSize: '1rem'
            }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem' }}>
          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: '500',
              color: 'var(--text-primary)'
            }}>
              PLZ *
            </label>
            <input
              type="text"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              placeholder="10115"
              maxLength={5}
              className="input"
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '8px',
                fontSize: '1rem'
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: '500',
              color: 'var(--text-primary)'
            }}>
              Stadt *
            </label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Berlin"
              className="input"
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '8px',
                fontSize: '1rem'
              }}
            />
          </div>
        </div>

        {/* Similar supermarkets */}
        {similarFound.length > 0 && (
          <div style={{
            backgroundColor: 'var(--bg-tertiary)',
            border: '1px solid var(--warning)',
            borderRadius: '8px',
            padding: '1rem'
          }}>
            <p style={{ margin: '0 0 0.75rem 0', fontWeight: '600', color: 'var(--text-primary)' }}>
              Ähnliche Supermärkte gefunden:
            </p>
            {similarFound.map((similar: { id: string; chain: string; name?: string; street: string; zipCode: string; city: string }) => (
              <div
                key={similar.id}
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  borderRadius: '6px',
                  padding: '0.75rem',
                  marginBottom: '0.5rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{similar.chain} {similar.name}</div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    {similar.street}, {similar.zipCode} {similar.city}
                  </div>
                </div>
                <button
                  onClick={() => onUseSimilar(similar)}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: 'var(--primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    cursor: 'pointer'
                  }}
                >
                  Verwenden
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={onNext}
          style={{
            width: '100%',
            padding: '1rem',
            backgroundColor: 'var(--primary)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            marginTop: '0.5rem'
          }}
        >
          Weiter
        </button>
      </div>
    </div>
  );
}

interface Step2ZonesProps {
  zones: Zone[];
  onAddZone: () => void;
  onRemoveZone: (index: number) => void;
  onUpdateZone: (index: number, field: keyof Zone, value: any) => void;
  onNext: () => void;
  onBack: () => void;
  draggedZoneIndex: number | null;
  dropTargetIndex: number | null;
  onDragStart: (index: number) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDrop: (e: React.DragEvent, dropIndex: number) => void;
  onDragEnd: () => void;
}

// Step 2: Zones Component
function Step2Zones({ zones, onAddZone, onRemoveZone, onUpdateZone, onNext, onBack, draggedZoneIndex, dropTargetIndex, onDragStart, onDragOver, onDrop, onDragEnd }: Step2ZonesProps) {
  return (
    <div style={{
      backgroundColor: 'var(--bg-primary)',
      borderRadius: '12px',
      padding: '2rem',
      boxShadow: 'var(--shadow-md)'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1.5rem'
      }}>
        <Layers size={24} style={{ color: 'var(--primary)' }} />
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>
          Bereiche im Laden
        </h2>
      </div>

      <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
        Erstelle die Bereiche in der Reihenfolge, wie du durch den Laden gehst.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
        {zones.map((zone: Zone, index: number) => (
          <div
            key={index}
            draggable
            onDragStart={() => onDragStart(index)}
            onDragOver={(e) => onDragOver(e, index)}
            onDrop={(e) => onDrop(e, index)}
            onDragEnd={onDragEnd}
            style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '8px',
              padding: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: draggedZoneIndex !== null ? 'grabbing' : 'grab',
              opacity: draggedZoneIndex === index ? 0.5 : 1,
              transform: draggedZoneIndex === index ? 'scale(0.98)' : 'scale(1)',
              boxShadow: draggedZoneIndex === index ? '0 4px 12px rgba(0, 0, 0, 0.2)' : undefined,
              borderTop: dropTargetIndex === index ? '3px solid var(--primary)' : '3px solid transparent',
              transition: 'all 0.2s ease'
            }}
          >
            <span
              style={{
                color: 'var(--text-secondary)',
                fontSize: '1.5rem',
                lineHeight: 1,
                userSelect: 'none',
                cursor: 'grab',
                flexShrink: 0
              }}
              title="Zone verschieben"
            >
              ⋮⋮
            </span>

            <div style={{
              width: '2.5rem',
              height: '2.5rem',
              backgroundColor: 'var(--bg-primary)',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.25rem',
              position: 'relative',
              flexShrink: 0
            }}
            title="Icon wird automatisch aus dem Zonennamen generiert"
            >
              {zone.icon}
            </div>

            <input
              type="text"
              value={zone.name}
              onChange={(e) => onUpdateZone(index, 'name', e.target.value)}
              placeholder={`Bereich ${index + 1}`}
              className="input"
              style={{
                flex: 1,
                minWidth: 0,
                padding: '0.625rem',
                borderRadius: '6px',
                fontSize: '1rem'
              }}
            />

            <button
              onClick={() => onRemoveZone(index)}
              style={{
                padding: '0.5rem',
                backgroundColor: 'transparent',
                border: '1px solid var(--error)',
                borderRadius: '6px',
                color: 'var(--error)',
                cursor: 'pointer',
                display: 'flex',
                flexShrink: 0
              }}
            >
              <X size={20} />
            </button>
          </div>
        ))}
      </div>

      <button
        onClick={onAddZone}
        style={{
          width: '100%',
          padding: '0.75rem',
          backgroundColor: 'var(--bg-tertiary)',
          color: 'var(--text-primary)',
          border: '1px dashed var(--border-color)',
          borderRadius: '8px',
          fontSize: '1rem',
          fontWeight: '500',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          marginBottom: '1.5rem'
        }}
      >
        <Plus size={20} />
        Bereich hinzufügen
      </button>

      <div style={{ display: 'flex', gap: '1rem' }}>
        <button
          onClick={onBack}
          style={{
            flex: 1,
            padding: '1rem',
            backgroundColor: 'var(--bg-primary)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Zurück
        </button>
        <button
          onClick={onNext}
          style={{
            flex: 1,
            padding: '1rem',
            backgroundColor: 'var(--primary)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Weiter
        </button>
      </div>
    </div>
  );
}

interface Step3CategoriesProps {
  zones: Zone[];
  currentZoneIndex: number;
  availableCategories: string[];
  onToggleCategory: (zoneIndex: number, category: string) => void;
  onNext: () => void;
  onBack: () => void;
  isLastZone: boolean;
  loading: boolean;
  showAddCategory: boolean;
  setShowAddCategory: (show: boolean) => void;
  newCategoryName: string;
  setNewCategoryName: (name: string) => void;
  onAddCustomCategory: () => void;
}

// Step 3: Categories Component
function Step3Categories({ 
  zones, 
  currentZoneIndex, 
  availableCategories, 
  onToggleCategory, 
  onNext, 
  onBack, 
  isLastZone, 
  loading,
  showAddCategory,
  setShowAddCategory,
  newCategoryName,
  setNewCategoryName,
  onAddCustomCategory
}: Step3CategoriesProps) {
  const currentZone = zones[currentZoneIndex];

  return (
    <div style={{
      backgroundColor: 'var(--bg-primary)',
      borderRadius: '12px',
      padding: '2rem',
      boxShadow: 'var(--shadow-md)'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1.5rem'
      }}>
        <Tags size={24} style={{ color: 'var(--primary)' }} />
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>
          Kategorien zuordnen
        </h2>
      </div>

      <div style={{
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem'
      }}>
        <span style={{ fontSize: '2rem' }}>{currentZone.icon}</span>
        <div>
          <div style={{ fontWeight: '600', fontSize: '1.125rem', color: 'var(--text-primary)' }}>{currentZone.name}</div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
            Bereich {currentZoneIndex + 1} von {zones.length}
          </div>
        </div>
      </div>

      <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
        Welche Produkt-Kategorien findest du in diesem Bereich?
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
        gap: '0.75rem',
        marginBottom: '1.5rem'
      }}>
        {availableCategories.map((category: string) => {
          const isSelected = currentZone.categories.includes(category);
          const standardCategory = getAllCategories().find(([name]) => name === category);
          const CategoryIcon = standardCategory ? getCategoryIcon(category) : null;
          
          return (
            <button
              key={category}
              onClick={() => onToggleCategory(currentZoneIndex, category)}
              style={{
                padding: '0.75rem',
                backgroundColor: isSelected ? 'var(--primary)' : 'var(--bg-secondary)',
                color: isSelected ? 'white' : 'var(--text-primary)',
                border: `1px solid ${isSelected ? 'var(--primary)' : 'var(--border-color)'}`,
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.5rem',
                transition: 'all 0.2s'
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {CategoryIcon ? (
                  <CategoryIcon size={16} />
                ) : (
                  <span>{getCategoryEmoji(category)}</span>
                )}
                {category}
              </span>
              {isSelected && <Check size={16} />}
            </button>
          );
        })}
      </div>

      {/* Add Custom Category */}
      {!showAddCategory ? (
        <button
          onClick={() => setShowAddCategory(true)}
          style={{
            width: '100%',
            padding: '0.75rem',
            backgroundColor: 'var(--bg-secondary)',
            color: 'var(--primary)',
            border: '2px dashed var(--primary)',
            borderRadius: '8px',
            fontSize: '0.875rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            marginBottom: '1.5rem'
          }}
        >
          <Plus size={18} />
          Eigene Kategorie hinzufügen
        </button>
      ) : (
        <div style={{
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && onAddCustomCategory()}
              placeholder="Kategoriename eingeben..."
              className="input"
              style={{ flex: 1 }}
              autoFocus
            />
            <button
              onClick={onAddCustomCategory}
              disabled={!newCategoryName.trim()}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: 'var(--primary)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: newCategoryName.trim() ? 'pointer' : 'not-allowed',
                opacity: newCategoryName.trim() ? 1 : 0.5
              }}
            >
              <Check size={18} />
            </button>
            <button
              onClick={() => {
                setShowAddCategory(false);
                setNewCategoryName('');
              }}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '1rem' }}>
        <button
          onClick={onBack}
          style={{
            flex: 1,
            padding: '1rem',
            backgroundColor: 'var(--bg-primary)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Zurück
        </button>
        <button
          onClick={onNext}
          disabled={loading}
          style={{
            flex: 1,
            padding: '1rem',
            backgroundColor: loading ? 'var(--text-secondary)' : 'var(--primary)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Erstelle...' : isLastZone ? 'Fertigstellen' : 'Nächster Bereich'}
        </button>
      </div>
    </div>
  );
}
